import { State } from '@progress/kendo-data-query';
import { FC, PropsWithChildren, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IsSupplierUser } from 'src/constants/permissionsList';
import { SearchItemFilterCategories } from 'src/constants/searchResultFilters';
import useDebounce from 'src/hooks/useDebounce';
import usePermission from 'src/hooks/usePermissions';
import useGetCatalogsQuery from 'src/services/catalog_products/useGetCatalogsQuery';
import { DropDownCategories, SetState } from 'src/types/generic';
import { getCatalogDropdownPlaceholder, transformCatalogsToTreeData } from 'src/utils/tansformCatalogsToTreeData';
import { createContext } from 'use-context-selector';
import {
  SearchProductFiltersContextShape,
  initialSearchListViewDataState,
  initialSearchPictureViewDataState,
} from './searchProductContext.types';
import {
  DEFAULT_SEARCH_CATALOG_OPTION,
  EMPTY_GRID_FILTER,
  buildToolbarFilters,
  getAttributesFilterKey,
  getEffectiveRefineSearch,
  getHasActiveFilters,
  removeFieldFromFilter,
  shouldClearAttributesForContractedItems,
  shouldResetItemFilterForAttributes,
} from './searchProductContext.utils';
import { SearchCatalogOption } from './searchProductData';

export const SearchProductFiltersContext = createContext<SearchProductFiltersContextShape>(
  {} as SearchProductFiltersContextShape
);

export const SearchProductFiltersProvider: FC<PropsWithChildren> = ({ children }): ReactElement => {
  const { t } = useTranslation();
  const isSupplierUser = usePermission({ permissions: IsSupplierUser });
  const [refineSearch, setRefineSearch] = useState('');
  const [selectedView, setSelectedView] = useState(0);
  const [dataState, setDataState] = useState<State>({ ...initialSearchListViewDataState });
  const [priceRangeFilter, setPriceRangeFilter] = useState('');
  const [itemFilter, setItemFilterState] = useState<DropDownCategories>(SearchItemFilterCategories[0]);
  const [selectedCatalogFilter, setSelectedCatalogFilter] = useState<SearchCatalogOption>(
    DEFAULT_SEARCH_CATALOG_OPTION
  );
  const [listViewTake, setListViewTake] = useState(100);
  const [pictureViewTake, setPictureViewTake] = useState(25);
  const [pendingFilterToastKey, setPendingFilterToastKey] = useState<string | null>(null);

  const debouncedRefineSearch = useDebounce(refineSearch.trim(), 500);
  const effectiveRefineSearch = getEffectiveRefineSearch(debouncedRefineSearch);
  const isRefineSearchActive = Boolean(effectiveRefineSearch);

  const { catalogs, isLoading: loadingCatalogs } = useGetCatalogsQuery();
  const catalogTreeData = useMemo(
    () =>
      transformCatalogsToTreeData(catalogs ?? [], {
        includeAllCatalogs: true,
        allCatalogsLabel: t('All Catalogs'),
      }),
    [catalogs, t]
  );
  const catalogDropdownPlaceholder = useMemo(
    () => getCatalogDropdownPlaceholder(catalogs, loadingCatalogs, t),
    [catalogs, loadingCatalogs, t]
  );

  const attributesFilterKey = useMemo(
    () => getAttributesFilterKey(dataState.filter),
    [dataState.filter]
  );
  const previousAttributesFilterKeyRef = useRef(attributesFilterKey);
  const itemFilterRef = useRef(itemFilter);
  itemFilterRef.current = itemFilter;

  useEffect(() => {
    const previousKey = previousAttributesFilterKeyRef.current;
    previousAttributesFilterKeyRef.current = attributesFilterKey;

    if (
      shouldResetItemFilterForAttributes(previousKey, attributesFilterKey) &&
      itemFilterRef.current.value === 1
    ) {
      setItemFilterState(SearchItemFilterCategories[0]);
      setPendingFilterToastKey('contractedItemsFilterReset');
    }
  }, [attributesFilterKey]);

  const setItemFilter = useCallback<SetState<DropDownCategories>>(
    (value) => {
      const nextValue = typeof value === 'function' ? value(itemFilterRef.current) : value;

      if (shouldClearAttributesForContractedItems(nextValue.value, attributesFilterKey)) {
        setDataState((prev) => ({
          ...prev,
          skip: 0,
          filter: removeFieldFromFilter(prev.filter, 'attributes') ?? EMPTY_GRID_FILTER,
        }));
        setPendingFilterToastKey('attributeFilterCleared');
      }

      setItemFilterState(nextValue);
    },
    [attributesFilterKey]
  );

  const clearPendingFilterToast = useCallback(() => {
    setPendingFilterToastKey(null);
  }, []);

  const toolbarFilters = useMemo(
    () =>
      buildToolbarFilters({
        catalogId: selectedCatalogFilter.catalogId,
        itemFilterValue: itemFilter.value,
        priceRangeFilter,
      }),
    [itemFilter.value, priceRangeFilter, selectedCatalogFilter.catalogId]
  );

  const defaultSort = useMemo(
    () =>
      (selectedView === 0
        ? initialSearchListViewDataState
        : initialSearchPictureViewDataState).sort,
    [selectedView]
  );

  const hasActiveFilters = useMemo(
    () =>
      getHasActiveFilters({
        refineSearch,
        priceRangeFilter,
        itemFilterValue: itemFilter.value,
        catalogId: selectedCatalogFilter.catalogId,
        gridFilterCount: dataState.filter?.filters?.length ?? 0,
        sort: dataState.sort,
        defaultSort,
      }),
    [
      refineSearch,
      priceRangeFilter,
      itemFilter.value,
      selectedCatalogFilter.catalogId,
      dataState.filter?.filters,
      dataState.sort,
      defaultSort,
    ]
  );

  const clearFilters = useCallback(() => {
    setRefineSearch('');
    setPriceRangeFilter('');
    setItemFilterState(SearchItemFilterCategories[0]);
    setPendingFilterToastKey(null);
    setSelectedCatalogFilter(DEFAULT_SEARCH_CATALOG_OPTION);
    setDataState((prev) => ({
      ...(selectedView === 0
        ? initialSearchListViewDataState
        : initialSearchPictureViewDataState),
      take: prev.take,
    }));
  }, [selectedView]);

  const value = useMemo<SearchProductFiltersContextShape>(
    () => ({
      refineSearch,
      setRefineSearch,
      effectiveRefineSearch,
      isRefineSearchActive,
      selectedView,
      setSelectedView,
      dataState,
      setDataState,
      catalogTreeData,
      catalogDropdownPlaceholder,
      selectedCatalogFilter,
      setSelectedCatalogFilter,
      priceRangeFilter,
      setPriceRangeFilter,
      itemFilter,
      setItemFilter,
      pendingFilterToastKey,
      clearPendingFilterToast,
      clearFilters,
      hasActiveFilters,
      toolbarFilters,
      listViewTake,
      pictureViewTake,
      setListViewTake,
      setPictureViewTake,
      isSupplierUser,
    }),
    [
      refineSearch,
      effectiveRefineSearch,
      isRefineSearchActive,
      selectedView,
      dataState,
      catalogTreeData,
      catalogDropdownPlaceholder,
      selectedCatalogFilter,
      priceRangeFilter,
      itemFilter,
      setItemFilter,
      pendingFilterToastKey,
      clearPendingFilterToast,
      clearFilters,
      hasActiveFilters,
      toolbarFilters,
      listViewTake,
      pictureViewTake,
      isSupplierUser,
    ]
  );

  return (
    <SearchProductFiltersContext.Provider value={value}>
      {children}
    </SearchProductFiltersContext.Provider>
  );
};
